import {
  tableMemo,
  type Row,
  type RowData,
  type RowModel,
  type Table,
} from '@tanstack/react-table';

import {
  DATA_TABLE_DETAILS_ROW_PARITY,
  DATA_TABLE_DETAILS_ROW_IDENTIFIER,
  DATA_TABLE_ROW_EXPANDED_ID_SUFFIX,
} from '../../constants.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import type { TableMemoTable } from '../types/table.types.js';
import type { RowParity } from './row-details-types.js';

export function createRowDetailsRowModel<
  // oxlint-disable-next-line typescript/no-explicit-any
  TData extends RowData = any,
>(): (
  table: Table<TDataTableFeatures, TData>,
) => () => RowModel<TDataTableFeatures, TData> {
  return (table) => {
    return tableMemo({
      feature: 'DataTableRowDetailsFeature',
      table: table as unknown as TableMemoTable,
      fnName: 'table.getRowDetailsRowModel',
      memoDeps: () => [
        table.atoms.rowDetails?.get(),
        table.getPreRowDetailsRowModel(),
      ],
      fn: () => _createRowDetailsRowModel(table),
    });
  };
}

function _createRowDetailsRowModel<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
): RowModel<TDataTableFeatures, TData> {
  const preRowDetailsRowModel = table.getPreRowDetailsRowModel();

  if (!preRowDetailsRowModel.rows.length) {
    return preRowDetailsRowModel;
  }

  const { rows, flatRows, rowsById } = preRowDetailsRowModel;

  const detailsRowModel = expandRowDetails<TData>({
    rows,
    flatRows,
    rowsById,
  });

  return detailsRowModel;
}

function expandRowDetails<TData extends RowData>(
  rowModel: RowModel<TDataTableFeatures, TData>,
) {
  const detailsRows: Row<TDataTableFeatures, TData>[] = [];

  rowModel.rows.forEach((row, index) => {
    const parity: RowParity = index % 2 === 0 ? 'even' : 'odd';
    row[DATA_TABLE_DETAILS_ROW_PARITY] = parity;
    detailsRows.push(row);
    if (row.getIsDetailsOpen()) {
      const clonedRow = Object.create(Object.getPrototypeOf(row));

      const detailRow: Row<TDataTableFeatures, TData> = Object.assign(
        clonedRow,
        row,
        {
          id: `${row.id}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`,
          [DATA_TABLE_DETAILS_ROW_IDENTIFIER]: true,
          // Make sure that the cloned row will not be selectable.
          getCanSelect: () => false,
          // Make sure that the cloned row will not show up in the subrows
          getCanExpand: () => false,
          // Make sure that the cloned row will be activateable
          getCanActivateRow: () => false,
          // Delegate to the original row so the aria-controls id matches the button's aria-controls.
          getRowDetailsAriaControlsId: () => row.getRowDetailsAriaControlsId(),
        },
      );
      detailsRows.push(detailRow);
      // Add the cloned row the the byId mapping as well, so that it can be looked up
      rowModel.rowsById[clonedRow.id] = clonedRow;
    }
  });

  return {
    rows: detailsRows,
    flatRows: rowModel.flatRows,
    rowsById: rowModel.rowsById,
  };
}
